import React, {
    useEffect,
    useState
} from "react";

import axios from "axios";

import {
    Search,
    CheckCircle,
    Clock,
    User,
    Mail,
    RefreshCw,
    BookOpen
} from "lucide-react";

import "./VisionIgniteEyeHealth.css";


const VisionIgniteEyeHealth = () => {

    /* =====================================================
       STATE
    ===================================================== */

    const [
        customers,
        setCustomers
    ] = useState([]);

    const [
        filteredCustomers,
        setFilteredCustomers
    ] = useState([]);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        error,
        setError
    ] = useState("");

    const [
        search,
        setSearch
    ] = useState("");

    const [
        levelFilter,
        setLevelFilter
    ] = useState("All");

    const [
        completingEmail,
        setCompletingEmail
    ] = useState(null);


    /* =====================================================
       LOAD CUSTOMERS ON PAGE LOAD
    ===================================================== */

    useEffect(() => {

        loadCustomers();

    }, []);


    /* =====================================================
       FILTER CUSTOMERS
    ===================================================== */

    useEffect(() => {

        let data = [...customers];


        /* =================================================
           SEARCH
        ================================================= */

        if (search.trim()) {

            const value =
                search
                    .trim()
                    .toLowerCase();

            data =
                data.filter(
                    (customer) => {

                        return (

                            customer.name
                                ?.toLowerCase()
                                .includes(value)

                            ||

                            customer.email
                                ?.toLowerCase()
                                .includes(value)

                        );

                    }
                );

        }


        /* =================================================
           LEVEL FILTER
        ================================================= */

        if (levelFilter !== "All") {

            data =
                data.filter(
                    (customer) =>

                        Number(
                            customer.currentLevel
                        ) ===
                        Number(
                            levelFilter
                        )
                );

        }


        setFilteredCustomers(
            data
        );

    }, [
        customers,
        search,
        levelFilter
    ]);


    /* =====================================================
       LOAD ALL VISION IGNITE CUSTOMERS

       IMPORTANT:

       UserJourney -> all users

       Backend API:
       GET /api/journey
    ===================================================== */

    const loadCustomers =
        async () => {

            try {

                setLoading(true);

                setError("");


                console.log(
                    "Loading Vision Ignite UserJourney customers..."
                );


                /* =================================================
                   GET ALL USERJOURNEY CUSTOMERS
                ================================================= */

                const response =
                    await axios.get(
                        "/api/journey"
                    );


                console.log(
                    "Vision Ignite Journey List:",
                    response.data
                );


                /* =================================================
                   VALIDATE RESPONSE
                ================================================= */

                if (
                    !response.data ||
                    !response.data.success
                ) {

                    throw new Error(

                        response.data?.message ||

                        "Failed to fetch Vision Ignite customers."

                    );

                }


                /* =================================================
                   GET CUSTOMERS
                ================================================= */

                const customerData =
                    response.data.customers ||
                    [];


                console.log(
                    "UserJourney Customers:",
                    customerData
                );


                /* =================================================
                   SAVE CUSTOMERS
                ================================================= */

                setCustomers(
                    customerData
                );

                setFilteredCustomers(
                    customerData
                );

            }

            catch (err) {

                console.error(
                    "Vision Ignite Customer Error:",
                    err
                );


                console.error(
                    "Status:",
                    err.response?.status
                );


                console.error(
                    "Response:",
                    err.response?.data
                );


                setError(

                    err.response
                        ?.data
                        ?.message

                    ||

                    err.message

                    ||

                    "Failed to fetch Vision Ignite customers."

                );

            }

            finally {

                setLoading(false);

            }

        };


    /* =====================================================
       MARK EYE HEALTH COURSE COMPLETE

       EXISTING JOURNEY UPDATE API:

       PATCH /api/journey/update
    ===================================================== */

    const markComplete =
        async (email) => {

            try {

                setCompletingEmail(
                    email
                );

                setError("");


                console.log(
                    "Marking Eye Health Course complete:",
                    email
                );


                /* =================================================
                   UPDATE USERJOURNEY
                ================================================= */

                const response =
                    await axios.patch(

                        "/api/journey/update",

                        {

                            email,

                            section:
                                "level2",

                            field:
                                "eyeHealthCourse",

                            value:
                                true

                        }

                    );


                console.log(
                    "Journey Update Response:",
                    response.data
                );


                /* =================================================
                   VALIDATE RESPONSE
                ================================================= */

                if (
                    !response.data ||
                    !response.data.success
                ) {

                    throw new Error(

                        response.data
                            ?.message

                        ||

                        "Failed to mark course complete."

                    );

                }


                /* =================================================
                   UPDATE UI
                ================================================= */

                setCustomers(
                    (prev) =>

                        prev.map(
                            (customer) => {

                                if (

                                    customer.email
                                        ?.toLowerCase() ===

                                    email
                                        ?.toLowerCase()

                                ) {

                                    return {

                                        ...customer,

                                        eyeHealthCourse:
                                            true,

                                        currentLevel:

                                            response.data
                                                ?.currentLevel

                                            ||

                                            customer.currentLevel

                                    };

                                }


                                return customer;

                            }
                        )

                );


                console.log(
                    "Eye Health Course successfully completed."
                );

            }

            catch (err) {

                console.error(
                    "Eye Health Course Error:",
                    err
                );


                console.error(
                    "Status:",
                    err.response?.status
                );


                console.error(
                    "Response:",
                    err.response?.data
                );


                setError(

                    err.response
                        ?.data
                        ?.message

                    ||

                    err.message

                    ||

                    "Failed to update course."

                );

            }

            finally {

                setCompletingEmail(
                    null
                );

            }

        };


    /* =====================================================
       REFRESH
    ===================================================== */

    const handleRefresh =
        () => {

            setSearch("");

            setLevelFilter("All");

            loadCustomers();

        };


    /* =====================================================
       LOADING
    ===================================================== */

    if (loading) {

        return (

            <div className="vih-page">

                <div className="vih-loading">

                    <RefreshCw
                        size={22}
                        className="vih-spin"
                    />

                    <span>

                        Loading Vision Ignite
                        customers...

                    </span>

                </div>

            </div>

        );

    }


    /* =====================================================
       STATISTICS
    ===================================================== */

    const totalCustomers =
        customers.length;


    const completedCustomers =
        customers.filter(
            (customer) =>
                customer.eyeHealthCourse === true
        ).length;


    const pendingCustomers =
        totalCustomers -
        completedCustomers;


    /* =====================================================
       PAGE
    ===================================================== */

    return (

        <div className="vih-page">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="vih-header">

                <div>

                    <div className="vih-title-row">

                        <div className="vih-title-icon">

                            <BookOpen
                                size={22}
                            />

                        </div>


                        <h1>

                            Eye Health Reset Course

                        </h1>

                    </div>


                    <p>

                        Manage and verify Eye Health Reset
                        Course completion for Vision Ignite members.

                    </p>

                </div>


                {/* =================================================
                    REFRESH
                ================================================= */}

                <button

                    type="button"

                    className="vih-refresh-btn"

                    onClick={
                        handleRefresh
                    }

                    disabled={
                        loading
                    }

                >

                    <RefreshCw
                        size={17}
                        className={
                            loading
                                ? "vih-spin"
                                : ""
                        }
                    />

                    Refresh

                </button>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="vih-error">

                    {error}

                </div>

            )}


            {/* =================================================
                STATS
            ================================================= */}

            <div className="vih-stats">


                {/* TOTAL CUSTOMERS */}

                <div className="vih-stat-card">

                    <div className="vih-stat-icon blue">

                        <User
                            size={21}
                        />

                    </div>


                    <div>

                        <span>

                            Total Customers

                        </span>


                        <strong>

                            {
                                totalCustomers
                            }

                        </strong>

                    </div>

                </div>


                {/* COMPLETED */}

                <div className="vih-stat-card">

                    <div className="vih-stat-icon green">

                        <CheckCircle
                            size={21}
                        />

                    </div>


                    <div>

                        <span>

                            Course Completed

                        </span>


                        <strong>

                            {
                                completedCustomers
                            }

                        </strong>

                    </div>

                </div>


                {/* PENDING */}

                <div className="vih-stat-card">

                    <div className="vih-stat-icon orange">

                        <Clock
                            size={21}
                        />

                    </div>


                    <div>

                        <span>

                            Pending

                        </span>


                        <strong>

                            {
                                pendingCustomers
                            }

                        </strong>

                    </div>

                </div>

            </div>


            {/* =================================================
                FILTER AREA
            ================================================= */}

            <div className="vih-filter-card">


                {/* SEARCH */}

                <div className="vih-search">

                    <Search
                        size={18}
                    />


                    <input

                        type="text"

                        placeholder="Search by name or email..."

                        value={
                            search
                        }

                        onChange={
                            (e) =>
                                setSearch(
                                    e.target.value
                                )
                        }

                    />

                </div>


                {/* LEVEL FILTER */}

                <select

                    className="vih-level-select"

                    value={
                        levelFilter
                    }

                    onChange={
                        (e) =>
                            setLevelFilter(
                                e.target.value
                            )
                    }

                >

                    <option value="All">

                        All Levels

                    </option>


                    <option value="1">

                        Level 1

                    </option>


                    <option value="2">

                        Level 2

                    </option>


                    <option value="3">

                        Level 3

                    </option>


                    <option value="4">

                        Level 4

                    </option>

                </select>

            </div>


            {/* =================================================
                TABLE
            ================================================= */}

            <div className="vih-table-card">


                {/* TABLE HEADER */}

                <div className="vih-table-header">

                    <div>

                        <h2>

                            Vision Ignite Customers

                        </h2>


                        <span>

                            {
                                filteredCustomers.length
                            }

                            {" "}

                            customers

                        </span>

                    </div>

                </div>


                {/* TABLE */}

                <div className="vih-table-scroll">

                    <table className="vih-table">


                        <thead>

                            <tr>

                                <th>
                                    Customer
                                </th>

                                <th>
                                    Level
                                </th>

                                <th>
                                    Course
                                </th>

                                <th>
                                    Status
                                </th>

                                <th>
                                    Action
                                </th>

                            </tr>

                        </thead>


                        <tbody>


                            {/* =================================================
                                EMPTY
                            ================================================= */}

                            {filteredCustomers.length === 0 ? (

                                <tr>

                                    <td

                                        colSpan="5"

                                        className="vih-empty"

                                    >

                                        No customers found.

                                    </td>

                                </tr>

                            ) : (

                                filteredCustomers.map(
                                    (customer) => {


                                        const isCompleting =

                                            completingEmail
                                                ?.toLowerCase() ===

                                            customer.email
                                                ?.toLowerCase();


                                        return (

                                            <tr

                                                key={
                                                    customer.email
                                                }

                                            >


                                                {/* =================================================
                                                    CUSTOMER
                                                ================================================= */}

                                                <td>

                                                    <div className="vih-customer">


                                                        <div className="vih-avatar">

                                                            <User
                                                                size={17}
                                                            />

                                                        </div>


                                                        <div>

                                                            <strong>

                                                                {
                                                                    customer.name ||
                                                                    "N/A"
                                                                }

                                                            </strong>


                                                            <span>

                                                                <Mail
                                                                    size={12}
                                                                />

                                                                {
                                                                    customer.email
                                                                }

                                                            </span>

                                                        </div>

                                                    </div>

                                                </td>


                                                {/* =================================================
                                                    LEVEL
                                                ================================================= */}

                                                <td>

                                                    <span className="vih-level">

                                                        Level{" "}

                                                        {
                                                            customer.currentLevel ||
                                                            1
                                                        }

                                                    </span>

                                                </td>


                                                {/* =================================================
                                                    COURSE
                                                ================================================= */}

                                                <td>

                                                    <div className="vih-course">

                                                        <BookOpen
                                                            size={15}
                                                        />

                                                        Eye Health
                                                        Reset Course

                                                    </div>

                                                </td>


                                                {/* =================================================
                                                    STATUS
                                                ================================================= */}

                                                <td>

                                                    {

                                                        customer.eyeHealthCourse ===
                                                        true

                                                            ? (

                                                                <span className="vih-status completed">

                                                                    <CheckCircle
                                                                        size={14}
                                                                    />

                                                                    Completed

                                                                </span>

                                                            )

                                                            : (

                                                                <span className="vih-status pending">

                                                                    <Clock
                                                                        size={14}
                                                                    />

                                                                    Not Completed

                                                                </span>

                                                            )

                                                    }

                                                </td>


                                                {/* =================================================
                                                    ACTION
                                                ================================================= */}

                                                <td>

                                                    {

                                                        customer.eyeHealthCourse ===
                                                        true

                                                            ? (

                                                                <span className="vih-done">

                                                                    <CheckCircle
                                                                        size={16}
                                                                    />

                                                                    Completed

                                                                </span>

                                                            )

                                                            : (

                                                                <button

                                                                    type="button"

                                                                    className="vih-complete-btn"

                                                                    disabled={
                                                                        isCompleting
                                                                    }

                                                                    onClick={() =>
                                                                        markComplete(
                                                                            customer.email
                                                                        )
                                                                    }

                                                                >

                                                                    {

                                                                        isCompleting

                                                                            ? (

                                                                                <RefreshCw

                                                                                    size={15}

                                                                                    className="vih-spin"

                                                                                />

                                                                            )

                                                                            : (

                                                                                <CheckCircle
                                                                                    size={15}
                                                                                />

                                                                            )

                                                                    }


                                                                    {

                                                                        isCompleting

                                                                            ? "Updating..."

                                                                            : "Mark Complete"

                                                                    }

                                                                </button>

                                                            )

                                                    }

                                                </td>

                                            </tr>

                                        );

                                    }

                                )

                            )}

                        </tbody>

                    </table>

                </div>

            </div>

        </div>

    );

};


export default VisionIgniteEyeHealth;